import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { CHAINS } from '../../../constants';
import { useBackendApi } from '../../../helpers/backendHelper';
import { useGlobalState } from '../../../state/GlobalStateProvider';
import { formatDecimals, formatNumber, reviewTimestamp, santizeHTML, trackClairtyEvent } from '../../../utils';
import { Flexbox, FlexboxColumn } from '../../action/Flexbox';
import { Line } from '../../action/Line';
import Logo from '../../Logo/Logo';
import ProjectTag from '../../ProjectTag/ProjectTag';
import ThemedText from '../../theme/ThemedText';
import ThemeLogo from '../../theme/ThemeLogo';
import ImageAttachments from '../ImageAttachments/ImageAttachments';
import ProjectChip from '../Projectchip/ProjectChip';
import SupertakeChip from '../Projectchip/SupertakeChip';
import CommentWithReadMore from './Comment';
import Reactions from './Reactions';
import './ReviewCard.scss';
import ReviewCardSkeleton from './ReviewCardSkelton';
import Voting from './Voting';

const ReviewCardDetails = forwardRef(({ maxWidth = false, showNoOfReviews = true, data, isDetailed = true }, ref) => {
  const navigate = useNavigate();
  const location = useLocation();
  const passedState = location.state;
  const { uuid } = useParams();

  const [{ user, protocolMapping }] = useGlobalState();
  const backendHelper = useBackendApi();

  const [openCommentSection, setOpenCommentSection] = useState(false);
  const [postData, setPostData] = useImmer();
  const localRef = useRef(null);
  const postCommentRef = useRef(null);

  const handleOpenCommentSection = () => {
    setOpenCommentSection(!openCommentSection);
    postCommentRef?.current?.expand();
  };

  function handleProtocol(project) {
    if (String(project).startsWith('chain')) {
      const chainId = Number(String(project).split('-')?.[1]);
      return CHAINS[chainId];
    } else {
      return protocolMapping[project];
    }
  }

  async function deleteComment(commentId) {
    if (!commentId) return;

    try {
      await backendHelper.reviews.deleteComment(commentId);
      setPostData(state => {
        state.comments = [];
      });
      alert.success('Comment deleted');
    } catch (error) {
      alert.error('Something went wrong!');
    }
  }

  function handleRedirect(address) {
    const url = `${window.location.origin}/profile/${address}/projects`;
    trackClairtyEvent('user_profile_visited', {
      origin: 'takes_user',
      is_own_profile: Boolean(address === user?.address),
    });
    window.open(url, '_blank');
  }

  async function getPostData() {
    try {
      const body = {
        filter: {
          uuid,
          pagination: {
            page: 1,
            limit: 5,
          },
        },
      };
      const response = await backendHelper.reviews.getFeed(body);
      setPostData(response?.data?.[0]);
    } catch (error) {}
  }

  function handleBack() {
    navigate(-1);
  }

  function handleTwitter() {
    window.open('https://discord.gg/0xmercle', '_target');
  }

  useEffect(() => {
    if (data) return setPostData(JSON.parse(JSON.stringify(data)));
    if (postData) return;
    if (!protocolMapping) return;
    if (passedState) {
      return setPostData(passedState);
    }
    getPostData();
  }, [passedState, uuid, protocolMapping, data]);

  useEffect(() => {
    if (!postData) return;
    if (user === undefined) return;

    trackClairtyEvent('detailed_take', {
      post_id: postData?.id,
      project: postData?.protocol,
      reaction_user_state: postData?.userReaction || 'no_reaction',
      user_state: user ? 'logged_in' : 'logged_out',
      take_type: 'user_take',
      tag: postData?.tags?.length > 0 && `${postData?.tags?.[0]}`,
    });
  }, [postData, user]);

  useEffect(() => {
    if (!ref) return;

    if (typeof externalRef === 'function') {
      ref(localRef.current);
    } else {
      ref.current = localRef.current;
    }
  }, [ref]);
  if (!postData)
    return (
      <FlexboxColumn fw alignItems={'flex-start'} className='feed_wrapper'>
        <ReviewCardSkeleton />
      </FlexboxColumn>
    );

  return (
    <FlexboxColumn fw alignItems={'flex-start'} className='feed_wrapper' justifyContent='flex-start'>
      {passedState && (
        <Flexbox className='pointer' onClick={handleBack} gap='0.5rem'>
          <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
            <path d='M12.6668 8H3.3335' stroke='#888888' stroke-width='1.3' stroke-linecap='round' stroke-linejoin='round' />
            <path d='M8.00016 12.6668L3.3335 8.00016L8.00016 3.3335' stroke='#888888' stroke-width='1.3' stroke-linecap='round' stroke-linejoin='round' />
          </svg>
          <ThemedText.P color='color-xlight' weight='semi-bold'>
            Back
          </ThemedText.P>
        </Flexbox>
      )}
      <FlexboxColumn ref={localRef} className={`reviewcard_wrapper ${maxWidth && 'review_wrapper_width'}`} gap='1.5rem'>
        <Flexbox justifyContent='space-between' fw className='reviewcard_wrapper_heading'>
          <Flexbox gap='0.5rem' className='reviewcard_wrapper_user' onClick={() => handleRedirect(postData?.userAddress)}>
            <Logo src={postData?.userAvatarUrl} width={'1.5rem'} height={'1.5rem'} borderRadius='50%' />
            <Flexbox gap='0.5rem' className='reviewcard_wrapper_user_name'>
              <ThemedText.H6 weight='medium'>{postData?.userName}</ThemedText.H6>

              {postData?.superTake ? (
                <SupertakeChip project={postData?.protocol} tags={postData?.tags} />
              ) : (
                <ProjectChip project={postData?.protocol} tags={postData?.tags || []} />
              )}
            </Flexbox>

            <ThemedText.H6 className='reviewcard_wrapper_light'>{reviewTimestamp({ timestamp: parseInt(postData?.timestamp) })}</ThemedText.H6>
          </Flexbox>
          {postData?.superTake ? (
            <ThemeLogo className={'pointer'} icon={'supertakeYellow'} />
          ) : (
            <>
              {postData?.protocol === 'zora' ? (
                <div></div>
              ) : (
                <ThemedText.H6 color='color-xlight'>
                  ${formatNumber(formatDecimals(postData?.airdropDollarValue || postData?.userVol))} {postData?.airdropDollarValue ? 'Airdrop claimed' : 'Vol'} on{' '}
                  {handleProtocol(postData?.protocol)?.name}
                </ThemedText.H6>
              )}
            </>
          )}
        </Flexbox>
        {/* {postData?.superTake && <SupetakeTile data={postData?.data} />} */}

        <div className='reviewcard_wrapper_review'>
          {postData?.header && <ThemedText.H5 weight='medium'>{postData?.header}</ThemedText.H5>}
          <div className='reviewcard_wrapper_review_text' dangerouslySetInnerHTML={{ __html: santizeHTML(postData?.content) }} />
          <ImageAttachments attachments={postData?.attachments} />
        </div>
        {postData?.comments?.length > 0 && (
          <>
            <FlexboxColumn fw gap='0.75rem'>
              <Flexbox justifyContent='space-between' fw>
                <Flexbox gap='0.5rem' className='reviewcard_wrapper_user' onClick={() => handleRedirect(postData?.comments?.[0]?.user?.address)}>
                  <Logo src={postData?.comments?.[0]?.user?.avatarUrl} width={'1.5rem'} height={'1.5rem'} borderRadius='50%' />
                  <ThemedText.H6 weight='medium'>{postData?.comments?.[0]?.user?.name}</ThemedText.H6>
                  <ProjectTag project={postData?.protocol} />
                  <ThemedText.H6 className='reviewcard_wrapper_light'>replied</ThemedText.H6>
                </Flexbox>
                {user?.address === postData?.comments?.[0]?.user?.address && postData?.comments?.[0]?.commentid && (
                  <ThemedText.H6 onClick={() => deleteComment(postData?.comments?.[0]?.commentid)} className='reviewcard_wrapper_light pointer'>
                    delete reply
                  </ThemedText.H6>
                )}
              </Flexbox>
              <div className='reviewcard_wrapper_review'>
                <CommentWithReadMore content={postData?.comments?.[0]?.content} />
                <ImageAttachments attachments={postData?.attachments} />
              </div>
            </FlexboxColumn>
            <Line />
          </>
        )}
        <Reactions postData={postData} handlePostData={setPostData} isDetailed={isDetailed || data} />
        <Line />
        <Voting postData={postData} />
        <Line />
        <Flexbox fw className='pointer' justifyContent='flex-start' onClick={handleTwitter}>
          <ThemedText.H6 weight='semi-bold' color='color-xlight'>
            Get Support
          </ThemedText.H6>
        </Flexbox>
      </FlexboxColumn>
    </FlexboxColumn>
  );
});

export default ReviewCardDetails;
